import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../../services/window-information';
import FormActions from '../../../../components/FormActions';
import MyAppText from '../../../../components/MyAppText';
import { translate } from '../../../../services/translate';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import { appAgentService, InvalidApplication } from '../../../../services/central-api/app-agent';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onConfirm: () => void;
    vacancy: Vacancy;
}

export default function VacancyApplyModal({ isModalVisible, setModalVisible, vacancy, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const remainingVacancies = vacancy.quantity - (vacancy.applicationsCount ?? 0);

    const [step, setStep] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const closeModal = () => {
        setModalVisible(false);
        setStep(0);
    };

    function getInvalidApplicationErrorMessage(message: string) {
        if (message === 'tooManyAbsences') {
            return translate('tooManyAbsences');
        } else if (message === 'tooManyApplications') {
            return translate('tooManyApplications', { date: moment(vacancy.activityStart).format('MM/YY') });
        } else if (message === 'alreadyApplied') {
            return translate('alreadyApplied');
        } else if (message === 'vacancyNotApplicable') {
            return translate('vacancyNotApplicable');
        }

        return translate('unexpectedError');
    }

    async function vacancyApply() {
        try {
            setIsLoading(true);
            await appAgentService.applyToVacancy(vacancy.id);
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof InvalidApplication) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: getInvalidApplicationErrorMessage(err.message)
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }
    return (
        <>
            <Modal
                transparent
                visible={isModalVisible}
                animationType='fade'
                onRequestClose={() => closeModal()}
            >
                <View style={styles.container}>
                    <View style={styles.formContainer(windowInfo.isMobile)}>
                        {/* header */}
                        <View style={styles.header}>
                            <MyAppText style={styles.headerTitle}>{translate('applyToVacancy')}</MyAppText>
                            <MyAppText style={styles.headerSubTitle}>{translate(step === 0 ? 'seeTheVacancySummary' : 'seeTheVacanciesAvailableForApplications')}</MyAppText>
                            <MyAppText style={styles.headerSubTitle}>{translate('stepXofY', { x: step + 1, y: 2 })}</MyAppText>
                        </View>
                        {/* Content */}
                        {step === 0
                            ? <View style={styles.content}>
                                <View style={{ flexDirection: 'column', gap: 16 }}>
                                    <MyAppText style={styles.contentTitle}>{vacancy.title}</MyAppText>
                                    <MyAppText style={styles.contentLightText}>{vacancy.workplace.name}</MyAppText>
                                    <MyAppText style={styles.contentLightText}>{vacancy.workplace.actingBodyUnit.name}</MyAppText>
                                    <MyAppText style={styles.contentLightText}>{vacancy.workplace.address}</MyAppText>
                                    <MyAppText style={styles.contentLightText}>{moment(vacancy.activityStart).format('DD/MM')}</MyAppText>
                                </View>
                                <MyAppText style={styles.contentLighterText}>{vacancy.description}</MyAppText>
                            </View>
                            : <View style={{ flexDirection: 'column', gap: 4 }}>
                                <MyAppText style={styles.contentTitleStep2}>{translate('availableVacancies')}</MyAppText>
                                <MyAppText style={styles.contentSubTitleStep2}>{remainingVacancies} {translate('vacancies')}</MyAppText>
                            </View>
                        }
                        <FormActions
                            viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                            buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                            buttonContentContainerStyle={{ flex: 1 }}
                            onSubmit={() => step === 0 ? setStep(1) : vacancyApply()}
                            onClose={() => step === 1 ? setStep(0) : closeModal()}
                            disabled={false}
                            isLoading={isLoading}
                            cancelText={step === 0 ? 'cancel' : 'goBack'}
                            confirmText={step === 0 ? 'forward' : 'apply'}
                        />
                    </View>
                </View>

            </Modal>
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
        borderRadius: 8
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        flexDirection: 'column',
        gap: 8,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    headerSubTitle: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    content: {
        padding: 8,
        flexDirection: 'column',
        gap: 16,
        backgroundColor: theme.colors.backgrounds['BG-2'],
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.neutral['100']
    },
    contentTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    contentLightText: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral['500']
    },
    contentLighterText: {
        fontSize: 12,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    contentTitleStep2: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.neutral['500']
    },
    contentSubTitleStep2: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
}));
